import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import {
    AlertTriangle,
    BarChart3,
    ClipboardCheck,
    History,
    Inbox,
    LayoutGrid,
    ListChecks,
    Search,
    Table2,
} from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import { httpClient } from '../../common/httpclient';
import { FilterChips } from '../../component/filter/FilterToolbar';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import ChecklistHistoryModal from '../vehicles/checklist/ChecklistHistoryModal';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { KitField, KitInput, KitSelect } from '../../component/common/MobileKitExtras';
import MobileReport, { MobileReportBody, useMobileRegenerate } from '../../component/report/MobileReport';
import { Caption, ChartCard, HBarsLimited } from '../../component/report/MobileSalesSections';
import { ChecklistModelList, NeverUsedList } from '../../component/report/MobileChecklistSections';
import { KitSeg } from '../../component/common/MobileKitExtras';
import { KitEmptyNote } from '../../component/common/MobileDashboardKit';

/**
 * Cobertura de dados x protótipo (`prototipo-relatorio-checklist.html`):
 *
 * O protótipo desenha uma linha por ORDEM DE SERVIÇO (Ordem/Cliente/Veículo/
 * Qtd. de preenchimentos/Última atualização/Preenchido por/Resultado
 * OK-NOK-Manutenção), com drill-down abrindo o histórico item-a-item daquela
 * ordem. O endpoint real, `GET /checklist/report` (`checklist/reporter.go` +
 * `ChecklistRepository.GetChecklistReport`), devolve o inverso: uma linha
 * por MODELO de checklist cadastrado (`checklist_name`, `description`,
 * `inspection_groups_count`, `times_used_in_service_order`, `mechanic` — só
 * o último mecânico que preencheu), agregando OS por trás dos parâmetros
 * `customer_id`/`vehicle_id`. Não existe endpoint que devolva uma linha por
 * OS com resultado agregado, nem um filtro de período (a query só aceita
 * cliente/veículo). Por isso:
 *  - A tabela desta tela lista MODELOS de checklist (nome, grupos de
 *    inspeção, preenchimentos, último a preencher), não ordens de serviço.
 *  - O drill-down abre `ChecklistHistoryModal` (`GET /checklist/{id}/history`)
 *    por MODELO — mostra todos os preenchimentos daquele modelo (por OS/
 *    cliente/veículo/mecânico/data), exatamente o mesmo componente já usado
 *    antes do redesign, sem endpoint novo.
 *  - Não há filtro "Resultado" (OK/NOK/Manutenção): nenhum endpoint expõe
 *    resposta agregada por item nesse nível — só o preenchimento por OS
 *    aberto em `checklist/service-order/{id}` (usado na Operação da OS, fora
 *    de escopo aqui). Omitido em vez de fabricado.
 *  - Os 4 KPIs do protótipo (preenchidos no período / OK / NOK / Manutenção)
 *    não têm dado equivalente (sem período, sem resultado agregado);
 *    substituídos por 4 KPIs 100% reais: total de preenchimentos somados,
 *    modelos de checklist ativos, modelos nunca utilizados e média de grupos
 *    de inspeção por modelo.
 *  - A aba Gráficos do protótipo (preenchidos por mês, donut por resultado,
 *    por modelo, itens avaliados, itens por grupo) também depende de
 *    período/resultado/item — mantido só o gráfico "por modelo" (que É
 *    dado real: preenchimentos e grupos de inspeção por `checklist_name`),
 *    mais uma lista de modelos nunca utilizados (dado real, não estava no
 *    protótipo mas é um insight direto da mesma resposta).
 *  - Toolbar: o próprio item do FRONTEND-TASKS.md já restringe o escopo a
 *    "filtro por nome/cliente" (diferente do protótipo, que também mostra
 *    Veículo/Placa e Resultado). Preservado 1:1: a tela original já só
 *    renderizava os campos Nome do checklist + Cliente — havia um estado
 *    `selectedVehicle`/`vehicle_id` de backend só residual (nunca exposto em
 *    nenhum campo da UI, então sempre vazio); removido por ser código morto,
 *    sem nenhuma mudança de comportamento observável (o parâmetro
 *    `vehicle_id` já nunca era enviado antes).
 *
 * Nenhuma extensão de backend foi implementada nesta tarefa — é só
 * frontend, reaproveitando dado real já existente.
 */

/**
 * DESIGN-SYSTEM.md §6.1 "report-toolbar" micro-label — uppercase, estático,
 * renderizado fora do campo (não `InputLabel`). Mesmo padrão local já usado
 * por `CustomerReport.js`/`VehicleReport.js`/`SalesReport.js`.
 */
const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

const TB_FIELD_RADIUS = '9px';
const selectSx = { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS };
const textFieldSx = {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
        height: 40,
        fontSize: '13px',
        borderRadius: TB_FIELD_RADIUS,
        backgroundColor: 'background.subtle',
    },
};

// KPI card — DESIGN-SYSTEM.md §5.2 "KPI card": ícone chip 34px tintado no
// topo, valor grande neutro, legenda pequena. Mesmo componente local de
// `SalesReport.js`/`CustomerReport.js`/`VehicleReport.js`.
function KpiCard({ icon, iconBg, iconFg, value, label, loading }) {
    return (
        <Card>
            <CardContent sx={{ p: { xs: '10px', md: 2 }, '&:last-child': { pb: { xs: '10px', md: 2 } } }}>
                <Box sx={{
                    width: { xs: 28, md: 34 },
                    height: { xs: 28, md: 34 },
                    borderRadius: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: iconBg,
                    color: iconFg,
                    mb: { xs: '6px', md: 1.5 },
                }}>
                    {icon}
                </Box>
                {loading ? (
                    <Skeleton variant="text" width={90} height={30} sx={{ mb: 0.25 }} />
                ) : (
                    <Typography sx={{ fontSize: { xs: '17px', md: '20px' }, fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', mb: 0.25, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
                        {value}
                    </Typography>
                )}
                <Typography sx={{ fontSize: '11.5px', color: 'text.secondary' }}>
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
}

// `.chip-count` — contador arredondado usado nas colunas de quantidade.
function CountChip({ value, tone = 'blue' }) {
    const toneSx = tone === 'grey'
        ? { bgcolor: 'background.subtle', color: 'text.secondary' }
        : { bgcolor: 'info.bg', color: 'primary.main' };
    return (
        <Chip
            label={value}
            size="small"
            sx={{ minWidth: 30, height: 22, fontWeight: 800, fontSize: '12px', ...toneSx }}
        />
    );
}

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta).
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// Painel de gráfico genérico (`.panel-head`).
function ChartPanel({ title, subtitle, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={160} height={20} />
                            <Skeleton variant="text" width={200} height={14} />
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                            {subtitle ? (
                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                            ) : null}
                        </>
                    )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// Gráfico de barras genérico (contagem por checklist).
function CategoryBarChart({ data, xKey, title, subtitle, loading, height = 240, valueSuffix = '' }) {
    const theme = useTheme();
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : data && data.length > 0 ? (
                    <BarChart
                        dataset={data}
                        height={height - 20}
                        margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                        borderRadius={6}
                        hideLegend
                        xAxis={[{
                            scaleType: 'band',
                            dataKey: xKey,
                            categoryGapRatio: 0.5,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: 'count',
                            color: theme.palette.primary.main,
                            valueFormatter: (value) => `${value}${valueSuffix}`,
                        }]}
                        sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                    />
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem checklists cadastrados" />
                )}
            </Box>
        </ChartPanel>
    );
}

const miniHeadCellSx = { py: 1.25, px: 1.5, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', borderBottom: '1px solid', borderColor: 'divider' };
const miniBodyCellSx = { py: 1.25, px: 1.5, fontSize: '12.5px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' };

export default function ChecklistReport() {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [checklistData, setChecklistData] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [openHistoryModal, setOpenHistoryModal] = useState(false);
    const [selectedChecklistId, setSelectedChecklistId] = useState(null);

    // Filtros — só Nome do checklist + Cliente (mesmo escopo da tela antes
    // do redesign e do próprio item do FRONTEND-TASKS.md, "filtro por
    // nome/cliente"; o protótipo também mostra Veículo/Placa e Resultado,
    // ver comentário de cobertura no topo do arquivo).
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [checklistNameFilter, setChecklistNameFilter] = useState('');

    // Catálogo de checklists (id + nome) — carregado uma vez, usado para
    // resolver o `checklist_id` (por nome) exigido pelo drill-down
    // (`ChecklistHistoryModal` / `GET /checklist/{id}/history`), já que
    // `GET /checklist/report` não devolve o id. Mesmo endpoint (`GET
    // /checklist`) que a tela já chamava antes do redesign — só passou a
    // ser buscado uma vez, no carregamento, em vez de a cada clique em "Ver
    // histórico".
    const [checklistCatalog, setChecklistCatalog] = useState([]);

    // Carregar lista de clientes + catálogo de checklists
    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response = await httpClient.get('/customers');
                const customersList = response.data.map((customer) => ({
                    id: customer.ID,
                    name: customer.Name,
                }));
                setCustomers(customersList);
            } catch (error) {
                console.error('Erro ao carregar clientes:', error);
                setDialogMessage('Erro ao carregar lista de clientes.');
                setOpenDialog(true);
            }
        };
        const fetchChecklistCatalog = async () => {
            try {
                const response = await httpClient.get('/checklist');
                setChecklistCatalog(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Erro ao carregar catálogo de checklists:', error);
            }
        };
        fetchCustomers();
        fetchChecklistCatalog();
    }, []);

    // Buscar dados do relatório
    const fetchReportData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (selectedCustomer) {
                params.customer_id = selectedCustomer;
            }

            const response = await httpClient.get('/checklist/report', { params });
            setChecklistData(response.data || []);
        } catch (error) {
            console.error('Erro ao buscar dados do relatório:', error);
            setDialogMessage('Erro ao gerar relatório. Tente novamente.');
            setOpenDialog(true);
            setChecklistData([]);
        } finally {
            setLoading(false);
        }
    };

    // Buscar dados ao montar componente
    useEffect(() => {
        fetchReportData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtro local por nome do checklist, aplicado sobre o último resultado
    // buscado (cliente/veículo já são filtros de backend, só valem depois de
    // "Gerar relatório").
    const filteredData = useMemo(() => {
        const rows = checklistData.map((row, index) => ({ ...row, id: `${row.checklist_name}-${index}` }));
        if (!checklistNameFilter.trim()) return rows;
        const term = checklistNameFilter.trim().toLowerCase();
        return rows.filter((item) => item.checklist_name?.toLowerCase().includes(term));
    }, [checklistData, checklistNameFilter]);

    const handleSearch = () => {
        fetchReportData();
    };

    // Filtros (mobile): limpa os campos sem gerar o relatório.
    const handleClearFilters = () => {
        setSelectedCustomer('');
        setChecklistNameFilter('');
    };

    const getChecklistIdByName =(checklistName) => {
        const checklist = checklistCatalog.find((c) => c.name === checklistName);
        return checklist ? checklist.id : null;
    };

    const handleViewHistory = (row) => {
        const checklistId = getChecklistIdByName(row.checklist_name);

        if (!checklistId) {
            setDialogMessage('Checklist não encontrado.');
            setOpenDialog(true);
            return;
        }

        setSelectedChecklistId(checklistId);
        setOpenHistoryModal(true);
    };

    // KPIs — derivados 100% de `GET /checklist/report` (ver comentário de
    // cobertura no topo do arquivo).
    const totalFillings = useMemo(() => filteredData.reduce((sum, r) => sum + (r.times_used_in_service_order || 0), 0), [filteredData]);
    const totalTemplates = filteredData.length;
    const neverUsedCount = useMemo(() => filteredData.filter((r) => !r.times_used_in_service_order).length, [filteredData]);
    const avgGroups = useMemo(() => {
        if (filteredData.length === 0) return 0;
        const total = filteredData.reduce((sum, r) => sum + (r.inspection_groups_count || 0), 0);
        return total / filteredData.length;
    }, [filteredData]);

    // Gráficos — preenchimentos e grupos de inspeção por modelo de
    // checklist, únicos dados agregáveis por checklist que a API expõe.
    const fillingsByModel = useMemo(
        () => filteredData.map((r) => ({ label: r.checklist_name, count: r.times_used_in_service_order || 0 })),
        [filteredData]
    );
    const groupsByModel = useMemo(
        () => filteredData.map((r) => ({ label: r.checklist_name, count: r.inspection_groups_count || 0 })),
        [filteredData]
    );
    const neverUsedRows = useMemo(() => filteredData.filter((r) => !r.times_used_in_service_order), [filteredData]);

    const [activeTab, setActiveTab] = useState(0);

    const selectedCustomerName = customers.find((c) => c.id === selectedCustomer)?.name;

    const isMobileShell = useIsMobileShell();
    // Mobile: o relatório já carrega ao montar; "Aplicar" repete a mesma busca (cliente) do botão "Gerar".
    const regenerate = useMobileRegenerate(handleSearch, { enabled: isMobileShell, skipInitial: true });

    if (isMobileShell) {
        const barItems = (list) => list.map((item) => ({ name: item.label, value: item.count, text: String(item.count) }));
        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Lista de checklists' }, { value: 1, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>
                    {activeTab === 0 ? (
                        <ChecklistModelList key={`${filteredData.length}-${checklistNameFilter}`} rows={filteredData} onOpen={handleViewHistory} />
                    ) : (
                        <>
                            <ChartCard title="Preenchimentos por modelo de checklist" subtitle="Quantidade de ordens de serviço em que cada modelo foi preenchido">
                                {fillingsByModel.length > 0 ? <HBarsLimited items={barItems(fillingsByModel)} /> : <KitEmptyNote>Sem checklists cadastrados</KitEmptyNote>}
                            </ChartCard>
                            <ChartCard title="Grupos de inspeção por modelo" subtitle="Quantidade de grupos de inspeção cadastrados em cada modelo">
                                {groupsByModel.length > 0 ? <HBarsLimited items={barItems(groupsByModel)} /> : <KitEmptyNote>Sem checklists cadastrados</KitEmptyNote>}
                            </ChartCard>
                            <ChartCard title="Modelos nunca utilizados" subtitle="Modelos cadastrados que ainda não foram preenchidos em nenhuma ordem de serviço no filtro atual" bare>
                                <NeverUsedList rows={neverUsedRows} />
                            </ChartCard>
                        </>
                    )}
                </Box>
            </Box>
        );
        return (
            <PageScaffold title="Relatório de Checklist" titlePageTarget="Checklist" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                    extraFilters={(
                        <>
                            <KitField label="Nome do checklist">
                                <KitInput placeholder="Digite o nome do checklist" aria-label="Nome do checklist" value={checklistNameFilter} onChange={(e) => setChecklistNameFilter(e.target.value)} />
                            </KitField>
                            <KitField label="Cliente">
                                <KitSelect value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)} aria-label="Cliente">
                                    <option value="">Todos</option>
                                    {customers.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </KitSelect>
                            </KitField>
                        </>
                    )}
                >
                    <MobileReportBody
                        firstKpis
                        loading={loading}
                        ready={!loading}
                        kpis={[
                            { label: 'Preenchimentos', icon: 'checks', grad: true, value: totalFillings.toLocaleString('pt-BR') },
                            { label: 'Modelos ativos', icon: 'clipcheck', value: totalTemplates.toLocaleString('pt-BR') },
                            { label: 'Nunca utilizados', icon: 'alert', value: neverUsedCount.toLocaleString('pt-BR') },
                            { label: 'Grupos/modelo (média)', icon: 'grid', value: avgGroups.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) },
                        ]}
                        sections={[
                            {
                                type: 'node',
                                title: 'Detalhamento',
                                node: (
                                    <>
                                        <Caption>
                                            {totalTemplates} modelo(s) de checklist encontrado(s){selectedCustomerName ? ` · Cliente: ${selectedCustomerName}` : ''}
                                        </Caption>
                                        {tabsNode}
                                    </>
                                ),
                            },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
                <ChecklistHistoryModal
                    open={openHistoryModal}
                    onClose={() => {
                        setOpenHistoryModal(false);
                        setSelectedChecklistId(null);
                    }}
                    checklistId={selectedChecklistId}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Checklist"
            titlePageTarget="Checklist"
            links={[{ title: 'Relatórios', link: '/reports' }]}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                        <ClipboardCheck size={18} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Relatório de Checklist
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Modelos de checklist cadastrados, uso em ordens de serviço e histórico completo de preenchimentos por modelo.
                        </Typography>
                    </Box>
                </Box>

                {/* Faixa de filtros — sem período (a API não filtra por data): nome do checklist + cliente. */}
                <ReportFilterBar
                    showPeriod={false}
                    extraKey={selectedCustomer}
                    onGenerate={handleSearch}
                    loading={loading}
                    onClear={handleClearFilters}
                >
                    {/* Nome: filtro local (não exige gerar). Cliente: filtro de backend (entra no `extraKey`). */}
                    <Box className="rfb-group">
                        <TbLabel>Nome do checklist</TbLabel>
                        <TextField
                            size="small"
                            placeholder="Digite o nome do checklist"
                            value={checklistNameFilter}
                            onChange={(e) => setChecklistNameFilter(e.target.value)}
                            sx={textFieldSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search size={15} strokeWidth={2} color={theme.palette.text.disabled} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <Box className="rfb-group">
                        <TbLabel>Cliente</TbLabel>
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                            <Select
                                value={selectedCustomer}
                                displayEmpty
                                onChange={(e) => setSelectedCustomer(e.target.value)}
                                renderValue={(value) => customers.find((c) => c.id === value)?.name || 'Todos'}
                                sx={selectSx}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {customers.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </ReportFilterBar>

                {/* Chips de contexto — filtros ativos + total real devolvido pela API. */}
                <FilterChips sx={{ mb: { xs: 1.5, sm: 3 } }}>
                    <Chip
                        icon={<ClipboardCheck size={13} strokeWidth={2} />}
                        label={`${totalTemplates} modelo(s) de checklist encontrado(s)`}
                        size="small"
                        sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                    />
                    {selectedCustomerName && (
                        <Chip label={`Cliente: ${selectedCustomerName}`} size="small" sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }} />
                    )}
                </FilterChips>

                {/* KPI grid — DESIGN-SYSTEM.md §5.2/§6.1. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, md: 2 }, mb: { xs: 1.5, md: 3 } }}>
                    <KpiCard icon={<ClipboardCheck size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" value={<AnimatedNumber value={totalFillings} />} label="Preenchimentos em ordens de serviço" loading={loading} />
                    <KpiCard icon={<ListChecks size={17} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" value={<AnimatedNumber value={totalTemplates} />} label="Modelos de checklist ativos" loading={loading} />
                    <KpiCard icon={<AlertTriangle size={17} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" value={<AnimatedNumber value={neverUsedCount} />} label="Modelos nunca utilizados" loading={loading} />
                    <KpiCard icon={<LayoutGrid size={17} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" value={<AnimatedNumber value={avgGroups} decimals={1} format={(n) => n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} />} label="Média de grupos de inspeção por modelo" loading={loading} />
                </Box>

                {/* seg-tabs — DESIGN-SYSTEM.md §5.4. */}
                <Stack
                    direction="row"
                    sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', mb: 2.25, flexWrap: 'wrap' }}
                >
                    {[
                        { icon: Table2, label: 'Lista de checklists' },
                        { icon: BarChart3, label: 'Gráficos' },
                    ].map((tab, index) => {
                        const Icon = tab.icon;
                        const selected = activeTab === index;
                        return (
                            <Button
                                key={tab.label}
                                onClick={() => setActiveTab(index)}
                                startIcon={<Icon size={14} strokeWidth={2} />}
                                sx={{
                                    gap: '7px',
                                    padding: '9px 18px',
                                    lineHeight: 'normal',
                                    borderRadius: '8px',
                                    fontSize: '12.5px',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    color: selected ? 'primary.main' : 'text.secondary',
                                    bgcolor: selected ? 'background.paper' : 'transparent',
                                    boxShadow: selected ? (t) => t.customShadows?.sm : 'none',
                                    '&:hover': { bgcolor: selected ? 'background.paper' : 'background.subtle' },
                                }}
                            >
                                {tab.label}
                            </Button>
                        );
                    })}
                </Stack>

                {/* ===== TAB: LISTA DE CHECKLISTS ===== */}
                {activeTab === 0 && (
                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <DefaultTable
                                colunms={[
                                    {
                                        path: 'checklist_name',
                                        name: 'Checklist',
                                        render: (row) => (
                                            <Box>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{row.checklist_name || '—'}</Typography>
                                                {row.description && (
                                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>{row.description}</Typography>
                                                )}
                                            </Box>
                                        ),
                                    },
                                    {
                                        path: 'inspection_groups_count',
                                        name: 'Grupos de inspeção',
                                        render: (row) => <CountChip value={row.inspection_groups_count || 0} tone="grey" />,
                                    },
                                    {
                                        path: 'times_used_in_service_order',
                                        name: 'Preenchimentos',
                                        render: (row) => <CountChip value={row.times_used_in_service_order || 0} tone={row.times_used_in_service_order ? 'blue' : 'grey'} />,
                                    },
                                    {
                                        path: 'mechanic',
                                        name: 'Preenchido por',
                                        render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.mechanic || '—'}</Typography>,
                                    },
                                ]}
                                data={filteredData}
                                loading={loading}
                                rowPerPage={10}
                                rowsPerPageOptions={[5, 10, 25]}
                                itemLabel="checklists"
                                iconsActions={[{
                                    icon: <History size={16} strokeWidth={2} />,
                                    props: { onClick: handleViewHistory, tooltip: 'Ver histórico de preenchimentos', style: { cursor: 'pointer' } },
                                }]}
                                sx={{ boxShadow: 'none', border: 'none' }}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* ===== TAB: GRÁFICOS ===== */}
                {activeTab === 1 && (
                    <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                            <CategoryBarChart
                                data={fillingsByModel}
                                xKey="label"
                                title="Preenchimentos por modelo de checklist"
                                subtitle="Quantidade de ordens de serviço em que cada modelo foi preenchido"
                                loading={loading}
                            />
                            <CategoryBarChart
                                data={groupsByModel}
                                xKey="label"
                                title="Grupos de inspeção por modelo"
                                subtitle="Quantidade de grupos de inspeção cadastrados em cada modelo"
                                loading={loading}
                            />
                        </Box>

                        <Card>
                            <CardContent sx={{ p: 2.25 }}>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Modelos nunca utilizados</Typography>
                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px', mb: 2 }}>
                                    Modelos de checklist cadastrados que ainda não foram preenchidos em nenhuma ordem de serviço no filtro atual
                                </Typography>
                                {neverUsedRows.length === 0 ? (
                                    <EmptyMiniState icon={<Inbox size={22} strokeWidth={2} />} title="Nenhum modelo sem uso" subtitle="Todos os modelos filtrados já foram utilizados em ao menos uma OS" />
                                ) : (
                                    <TableContainer sx={{ maxHeight: 320, overflow: 'auto' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ ...miniHeadCellSx, bgcolor: 'background.paper' }}>Checklist</TableCell>
                                                    <TableCell sx={{ ...miniHeadCellSx, bgcolor: 'background.paper' }}>Descrição</TableCell>
                                                    <TableCell align="right" sx={{ ...miniHeadCellSx, bgcolor: 'background.paper' }}>Grupos de inspeção</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {neverUsedRows.map((row) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.checklist_name}</TableCell>
                                                        <TableCell sx={miniBodyCellSx}>{row.description || '—'}</TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}>{row.inspection_groups_count || 0}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>
                )}
            </Box>

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Informação"
                content={dialogMessage}
            />

            <ChecklistHistoryModal
                open={openHistoryModal}
                onClose={() => {
                    setOpenHistoryModal(false);
                    setSelectedChecklistId(null);
                }}
                checklistId={selectedChecklistId}
            />
        </PageScaffold>
    );
}
